import React, { useState } from "react";
import PracticeLayout from "../../../components/PracticeLayout";
import PracticeSection from "../../../components/PracticeSection";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/tabs";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "../../../components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../../../components/ui/dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../../../components/ui/tooltip";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useDocumentTitle from "../../../hooks/useDocumentTitle";

const PAGE_SIZE = 5;
const ITEMS = Array.from({ length: 27 }, (_, i) => `Row #${i + 1}`);
const SLIDES = [
  { title: "Test smart, not hard", body: "Locators built on data-testid outperform CSS or XPath every time." },
  { title: "Wait for state, not time", body: "sleep(2) is a bug; expect(locator).toBeVisible() is a test." },
  { title: "One assertion, one story", body: "Break long scenarios into small tests — flakiness scales with size." },
];

export default function LevelComplex() {
  useDocumentTitle("Level 4 · Complex Components — Practice");
  const [page, setPage] = useState(1);
  const [slide, setSlide] = useState(0);
  const [step, setStep] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  const totalPages = Math.ceil(ITEMS.length / PAGE_SIZE);
  const paged = ITEMS.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <PracticeLayout
      eyebrow="Level 4 · Complex Components"
      title="Composite widgets"
      subtitle="Tabs, accordions, modals — the day-to-day UI patterns your framework must handle robustly."
      seo={{
        title: "Level 4 · Complex Components — Tabs, Modal, Tooltip, Pagination",
        description: "Practise tabs, accordions, modals, tooltips, pagination, carousels and steppers with stable ARIA-friendly locators.",
        keywords: "modal dialog automation, tabs automation practice, tooltip automation, pagination automation",
        image: "/og/og-complex.svg",
      }}
    >
      <PracticeSection id="tabs" title="Tabs">
        <Tabs defaultValue="overview" data-testid="tabs-root">
          <TabsList data-testid="tabs-list">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" data-testid="tab-panel-overview" className="mt-3 text-sm text-zinc-700">Overview content — practice reading only the ACTIVE tab.</TabsContent>
          <TabsContent value="details" data-testid="tab-panel-details" className="mt-3 text-sm text-zinc-700">Details content — inactive panels have `hidden` attribute.</TabsContent>
          <TabsContent value="settings" data-testid="tab-panel-settings" className="mt-3 text-sm text-zinc-700">Settings content — assert on aria-selected.</TabsContent>
        </Tabs>
      </PracticeSection>

      <PracticeSection id="accordion" title="Accordion" hint="Only one panel open at a time (single mode).">
        <Accordion type="single" collapsible data-testid="accordion-root">
          {["what", "why", "how"].map((k) => (
            <AccordionItem key={k} value={k} data-testid={`accordion-item-${k}`}>
              <AccordionTrigger data-testid={`accordion-trigger-${k}`}>
                {{ what: "What is QaTestology?", why: "Why practise here?", how: "How do I start?" }[k]}
              </AccordionTrigger>
              <AccordionContent data-testid={`accordion-content-${k}`}>
                {{
                  what: "A free playground for QA/automation testers with stable locators.",
                  why: "Every widget mirrors what you'll meet in real apps.",
                  how: "Just start with Level 1 above — no signup for UI practice.",
                }[k]}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </PracticeSection>

      <PracticeSection id="modal" title="Modal (dialog)" hint="Body scroll should be locked while the modal is open.">
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogTrigger asChild>
            <Button data-testid="modal-open-btn" className="bg-[#002FA7] hover:bg-[#00227A] text-white">Open modal</Button>
          </DialogTrigger>
          <DialogContent data-testid="modal-content">
            <DialogHeader>
              <DialogTitle>Practice modal</DialogTitle>
              <DialogDescription>
                Modals trap focus. Try Tab to see focus stay inside, Escape to close.
              </DialogDescription>
            </DialogHeader>
            <Label>Your name</Label>
            <Input data-testid="modal-input" placeholder="Type something" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)} data-testid="modal-cancel-btn">Cancel</Button>
              <Button onClick={() => setModalOpen(false)} data-testid="modal-save-btn" className="bg-[#002FA7] hover:bg-[#00227A] text-white">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PracticeSection>

      <PracticeSection id="tooltip" title="Tooltip" hint="Radix appends the tooltip to a portal — search globally.">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" data-testid="tooltip-target">Hover for tooltip</Button>
            </TooltipTrigger>
            <TooltipContent data-testid="tooltip-content">This appears on hover / focus</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </PracticeSection>

      <PracticeSection id="pagination" title="Pagination">
        <div className="border border-zinc-200 rounded bg-white">
          <ul data-testid="pagination-list" className="divide-y divide-zinc-100">
            {paged.map((r) => <li key={r} className="px-3 py-2 text-sm">{r}</li>)}
          </ul>
          <div className="p-2 flex items-center justify-between border-t border-zinc-100">
            <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} data-testid="pagination-prev">
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  data-testid={`pagination-page-${p}`}
                  className={`h-7 min-w-7 px-2 rounded text-xs tag-mono ${p === page ? "bg-[#002FA7] text-white" : "hover:bg-zinc-100"}`}
                >
                  {p}
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="pagination-next">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div data-testid="pagination-info" className="tag-mono text-[11px] text-zinc-500 mt-1">page {page} / {totalPages}</div>
      </PracticeSection>

      <PracticeSection id="carousel" title="Carousel" hint="Advance with buttons; only the active slide is rendered.">
        <div className="border border-zinc-200 rounded p-6 bg-white text-center" data-testid="carousel-root">
          <div className="text-[10px] tag-mono uppercase tracking-widest text-zinc-400" data-testid="carousel-index">
            slide {slide + 1} of {SLIDES.length}
          </div>
          <div data-testid={`carousel-slide-${slide}`} className="mt-1">
            <div className="font-display font-bold text-lg">{SLIDES[slide].title}</div>
            <div className="text-sm text-zinc-600 mt-1">{SLIDES[slide].body}</div>
          </div>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button size="sm" variant="outline" onClick={() => setSlide((s) => (s - 1 + SLIDES.length) % SLIDES.length)} data-testid="carousel-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                data-testid={`carousel-dot-${i}`}
                className={`h-2 w-2 rounded-full ${i === slide ? "bg-[#002FA7]" : "bg-zinc-300"}`}
              />
            ))}
            <Button size="sm" variant="outline" onClick={() => setSlide((s) => (s + 1) % SLIDES.length)} data-testid="carousel-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </PracticeSection>

      <PracticeSection id="stepper" title="Stepper" hint="Complete each step to enable Next.">
        <div className="border border-zinc-200 rounded p-4 bg-white" data-testid="stepper-root">
          <div className="flex items-center gap-2 mb-4">
            {["Account", "Profile", "Confirm"].map((label, i) => (
              <div key={label} className="flex items-center gap-2" data-testid={`stepper-step-${i}`}>
                <span className={`h-7 w-7 rounded-full grid place-items-center text-xs tag-mono font-bold ${
                  i <= step ? "bg-[#002FA7] text-white" : "bg-zinc-100 text-zinc-500"
                }`}>{i + 1}</span>
                <span className={`text-sm ${i === step ? "font-semibold" : "text-zinc-500"}`}>{label}</span>
                {i < 2 && <span className="text-zinc-300">/</span>}
              </div>
            ))}
          </div>
          <div className="text-sm text-zinc-700 min-h-[60px]" data-testid="stepper-body">
            {step === 0 && "Step 1 — enter your email & password."}
            {step === 1 && "Step 2 — fill your profile."}
            {step === 2 && "Step 3 — confirm & submit."}
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="stepper-back">Back</Button>
            {step < 2 ? (
              <Button size="sm" onClick={() => setStep((s) => s + 1)} data-testid="stepper-next" className="bg-[#002FA7] hover:bg-[#00227A] text-white">Next</Button>
            ) : (
              <Button size="sm" onClick={() => setStep(0)} data-testid="stepper-finish" className="bg-emerald-600 hover:bg-emerald-700 text-white">Finish</Button>
            )}
          </div>
        </div>
      </PracticeSection>
    </PracticeLayout>
  );
}
